import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employesApi } from '../api/employes.api';
import { EmployeForm } from '../types';

export function useEmployes(
  filtres?: { actif?: boolean; recherche?: string; page?: number; limite?: number; idProjet?: string },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['employes', filtres],
    queryFn: () => employesApi.lister(filtres),
    enabled: options?.enabled !== false,
  });
}

export function useEmploye(id: string | undefined) {
  return useQuery({
    queryKey: ['employe', id],
    queryFn: () => employesApi.obtenirParId(id!),
    enabled: !!id,
  });
}

export function useCreerEmploye() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EmployeForm) => employesApi.creer(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employes'] }),
  });
}

export function useModifierEmploye(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<EmployeForm>) => employesApi.modifier(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employes'] });
      qc.invalidateQueries({ queryKey: ['employe', id] });
    },
  });
}

export function useDesactiverEmploye() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employesApi.desactiver(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employes'] }),
  });
}

export function useReactiverEmploye() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employesApi.reactiver(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employes'] }),
  });
}

export function useSupprimerEmploye() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employesApi.supprimerDefinitivement(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employes'] }),
  });
}

export function useImporterCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fichier: File) => employesApi.importerCsv(fichier),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employes'] }),
  });
}

export function useImporterExcel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fichier: File) => employesApi.importerExcel(fichier),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employes'] }),
  });
}

export function usePrimesEmploye(idEmploye: string | undefined) {
  return useQuery({
    queryKey: ['primes-employe', idEmploye],
    queryFn: () => employesApi.listerPrimes(idEmploye!),
    enabled: !!idEmploye,
  });
}

export function useAttribuerPrime(idEmploye: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => employesApi.attribuerPrime(idEmploye, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['primes-employe', idEmploye] }),
  });
}

export function useSuspendreAttribution(idEmploye: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idAttribution: string) => employesApi.suspendreAttribution(idEmploye, idAttribution),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['primes-employe', idEmploye] }),
  });
}

export function useOppositionsEmploye(idEmploye: string | undefined) {
  return useQuery({
    queryKey: ['oppositions-employe', idEmploye],
    queryFn: () => employesApi.listerOppositions(idEmploye!),
    enabled: !!idEmploye,
  });
}

export function useCreerOpposition(idEmploye: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => employesApi.creerOpposition(idEmploye, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oppositions-employe', idEmploye] }),
  });
}

export function useDesactiverOpposition(idEmploye: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idOpposition: string) =>
      employesApi.desactiverOpposition(idEmploye, idOpposition),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oppositions-employe', idEmploye] }),
  });
}
